/**
 * Record Pages Tab - Enhanced with Page Layout Assignment
 *
 * Manages record page assignments - determines which page layout to show
 * based on application context. Provides flexible assignment rules so the
 * same object can show different layouts in different applications.
 *
 * Two-Layer System:
 * - Page Layouts = Design templates (fields, sections, columns)
 * - Record Pages = Assignment rules (which layout to show when)
 */

import React, { useState } from 'react';
import { Plus, Edit, Trash2, AlertCircle, Layout as LayoutIcon, Star, Filter } from 'lucide-react';
import { useRecordPages, useSaveRecordPage, useDeleteRecordPage } from '../../../hooks/useRecordPages';
import { useLayoutsByObject } from '../../../services/queries/layoutQueries';
import { useApplications } from '../../../services/queries/applicationQueries';
import type { RecordPage } from '../../../types/recordPages';
import type { CustomObject } from '../../../services/queries/objectQueries';
import { DevModeBanner } from '../../common/DevModeBanner';
import { useToast } from '../../../contexts/ToastContext';

interface RecordPagesTabProps {
  objectId: string;
  object: CustomObject;
}

/**
 * Record Pages Tab Component
 *
 * Allows admins to create assignment rules that control which page layout
 * is displayed based on the application context (Global, CyberSweep, etc.)
 *
 * @component
 */
export const RecordPagesTab: React.FC<RecordPagesTabProps> = ({ objectId, object }) => {
  const [selectedApplication, setSelectedApplication] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecordPage, setEditingRecordPage] = useState<RecordPage | null>(null);

  const { data: applicationOptions = [] } = useApplications();
  const { data: recordPages = [], isLoading } = useRecordPages(
    object.apiName,
    selectedApplication
  );
  const { data: pageLayouts = [] } = useLayoutsByObject(objectId);

  const saveMutation = useSaveRecordPage();
  const deleteMutation = useDeleteRecordPage();
  const { success, error: showError } = useToast();

  const handleNew = () => {
    setEditingRecordPage(null);
    setIsModalOpen(true);
  };

  const handleEdit = (recordPage: RecordPage) => {
    setEditingRecordPage(recordPage);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      await deleteMutation.mutateAsync(id);
      success('Deleted', 'Record page deleted successfully');
    } catch (err: any) {
      showError('Error', err.message || 'Failed to delete record page');
    }
  };

  const handleSave = async (data: Partial<RecordPage>) => {
    try {
      await saveMutation.mutateAsync(data);
      success(
        editingRecordPage ? 'Updated' : 'Created',
        `Record page ${editingRecordPage ? 'updated' : 'created'} successfully`
      );
      setIsModalOpen(false);
      setEditingRecordPage(null);
    } catch (err) {
      showError('Error', 'Failed to save record page');
    }
  };

  return (
    <div className="p-6">
      {/* Status Banner */}
      <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900 dark:text-amber-100">Local storage only</p>
            <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
              Record page assignments are stored in browser localStorage and do not persist to Gobodo.
              For production layout management, use the <strong>Page Layouts</strong> tab — changes there are saved to Gobodo and reflected in applications.
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Record Pages
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Assign page layouts to {object.label} records based on application context
            </p>
          </div>

          <button
            onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Record Page
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <LayoutIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>Record Pages</strong> control which <strong>Page Layout</strong> is shown
                for {object.label} records. You can assign different layouts for different applications
                (e.g., CyberSweep vs Credit Repair) or based on field conditions.
              </p>
            </div>
          </div>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource="mockDataManager.ts"
          itemCount={`${recordPages.length} record pages`}
          customMessage={`Development Mode: Showing ${recordPages.length} record pages persisted via mockDataManager.ts (localStorage). Changes survive page refresh.`}
          dismissible={true}
        />

        {/* Application Filter */}
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Filter by Application:
          </label>
          <select
            value={selectedApplication}
            onChange={(e) => setSelectedApplication(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="All">All Applications</option>
            {applicationOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading record pages...</p>
        </div>
      ) : recordPages.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <LayoutIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No record pages found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {selectedApplication !== 'All'
              ? `No record pages for ${applicationOptions.find((opt) => opt.value === selectedApplication)?.label}`
              : 'Create your first record page to get started'}
          </p>
          {selectedApplication !== 'All' ? (
            <button
              onClick={() => setSelectedApplication('All')}
              className="text-purple-600 dark:text-purple-400 hover:underline font-medium"
            >
              View all record pages
            </button>
          ) : (
            <button
              onClick={handleNew}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Create Record Page
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {recordPages.map((recordPage) => (
            <div
              key={recordPage.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {recordPage.name}
                    </h4>
                    {recordPage.isDefault && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        Default
                      </span>
                    )}
                    {!recordPage.isActive && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                        Inactive
                      </span>
                    )}
                  </div>

                  {recordPage.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {recordPage.description}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Application:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {applicationOptions.find((opt) => opt.value === recordPage.application)?.label ||
                          recordPage.application}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Page Layout:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {pageLayouts.find((pl) => pl.id === recordPage.pageLayoutId)?.name || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Priority:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {recordPage.priority}
                      </span>
                    </div>
                  </div>

                  {recordPage.conditions && recordPage.conditions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Conditions:
                      </span>
                      <div className="mt-1 space-y-1">
                        {recordPage.conditions.map((cond, idx) => (
                          <div key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                            • {cond.field} {cond.operator} {String(cond.value)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(recordPage)}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>

                  {!recordPage.isDefault && (
                    <button
                      onClick={() => handleDelete(recordPage.id, recordPage.name)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Record Page Modal */}
      {isModalOpen && (
        <RecordPageModal
          recordPage={editingRecordPage}
          objectName={object.apiName}
          pageLayouts={pageLayouts}
          applicationOptions={applicationOptions}
          onClose={() => {
            setIsModalOpen(false);
            setEditingRecordPage(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

// ============================================
// RECORD PAGE MODAL COMPONENT
// ============================================

interface RecordPageModalProps {
  recordPage: RecordPage | null;
  objectName: string;
  pageLayouts: any[];
  applicationOptions: Array<{ value: string; label: string }>;
  onClose: () => void;
  onSave: (data: Partial<RecordPage>) => void;
}

const RecordPageModal: React.FC<RecordPageModalProps> = ({
  recordPage,
  objectName,
  pageLayouts,
  applicationOptions,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: recordPage?.name || '',
    description: recordPage?.description || '',
    application: recordPage?.application || 'all',
    pageLayoutId: recordPage?.pageLayoutId || '',
    priority: recordPage?.priority || 1,
    isActive: recordPage?.isActive ?? true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...recordPage,
      ...formData,
      objectName
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {recordPage ? 'Edit' : 'New'} Record Page
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., CyberSweep Client Page"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Application *
            </label>
            <select
              value={formData.application}
              onChange={(e) => setFormData({ ...formData, application: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {applicationOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Page Layout *
            </label>
            <select
              value={formData.pageLayoutId}
              onChange={(e) => setFormData({ ...formData, pageLayoutId: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Select a page layout...</option>
              {pageLayouts.map((layout) => (
                <option key={layout.id} value={layout.id}>
                  {layout.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The page layout that will be used for this record page
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Priority
            </label>
            <input
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              min="1"
              max="100"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Higher priority = higher precedence when multiple rules match (1-100)
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              {recordPage ? 'Save Changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
